import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContasContext } from 'contexts/ContasContext';
import { GridContext } from 'contexts/GridContext';
import { useModal } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import columnOverrides from './columnOverrides';
import getContasFilterFields from './filterFields';

const Contas = () => {
  const rotina = 'Contas';
  const titulo = 'Contas - Caixas e Bancos';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { postGrid, deleteGrid, exportPdf, exportXls } =
    useContext(GridContext);
  const { contas, getContas, getLoading } = useContext(ContasContext);

  const filterFields = useMemo(() => getContasFilterFields(), []);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getContas({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Adicionar',
      icon: 'add',
      placement: 'primary',
      action: () => navigate('/app/Tabelas/Contas/Manutencao'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: contas?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: contas?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={contas?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
  ];

  const rowActions = [
    {
      name: 'Editar',
      icon: 'edit',
      action: ({ row }) =>
        navigate('/app/Tabelas/Contas/Manutencao', { state: row }),
    },
    {
      name: 'Deletar',
      icon: 'delete',
      action: ({ row }) =>
        openModal(
          <DeleteModal
            onSubmit={() =>
              deleteGrid({
                params: { rotina, id: row?.id },
                cb: () => {
                  loadGrid();
                },
              })
            }
          />
        ),
    },
    {
      name: 'Ativar',
      icon: 'key',
      menu: true,
      show: ({ row }) => row?.ativo !== 'SIM',
      action: ({ row }) =>
        postGrid({
          data: { data: { ...row, ativo: 'SIM' }, rotina },
          cb: loadGrid,
        }),
    },
    {
      name: 'Inativar',
      icon: 'key_off',
      menu: true,
      show: ({ row }) => row?.ativo === 'SIM',
      action: ({ row }) =>
        postGrid({
          data: { data: { ...row, ativo: 'NAO' }, rotina },
          cb: loadGrid,
        }),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={contas?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={contas}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default Contas;

